import axios from "axios";
import { cacheSet, cacheGet, queueWrite, isOfflineCapable, runSync, emitSync } from "@/lib/offline";
import { startHeartbeat, reportNetSuccess, reportNetFailure } from "@/lib/connectivity";

// Use the configured backend URL, but when the app is served from a different
// (custom) domain — e.g. cms.hitechsilico.in / portal.hitechsilico.in — call the
// SAME origin so auth cookies stay first-party on every linked domain.
const envBase = process.env.REACT_APP_BACKEND_URL;
const resolveBase = () => {
  try {
    const envHost = new URL(envBase).hostname;
    const cur = window.location.hostname;
    if (cur !== envHost && cur !== "localhost" && cur !== "127.0.0.1") return window.location.origin;
  } catch {}
  return envBase;
};
const API = `${resolveBase()}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const API_URL = API;

let refreshing = null;
const cacheKey = (cfg) => (cfg.url || "").startsWith("/") ? (cfg.url || "") : `/${cfg.url || ""}`;

api.interceptors.response.use(
  async (r) => {
    reportNetSuccess();
    if (r.config?.method === "get" && !(r.config.responseType === "blob")) {
      cacheSet(cacheKey(r.config), r.data);
    }
    return r;
  },
  async (err) => {
    const cfg = err.config || {};
    const url = cfg.url || "";
    if (err.response?.status === 401 && !cfg._retried && !url.includes("/auth/") && !url.includes("/portal")) {
      cfg._retried = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
        await refreshing;
        return api(cfg);
      } catch {}
    }
    // Network failure → offline behaviour
    if (!err.response && !cfg._skipOffline) {
      reportNetFailure();
      const method = (cfg.method || "get").toLowerCase();
      if (method === "get" && cfg.responseType !== "blob") {
        const data = await cacheGet(cacheKey(cfg));
        if (data !== undefined) {
          emitSync({ type: "offline-read" });
          return { data, status: 200, statusText: "OK (offline cache)", headers: {}, config: cfg, _offline: true };
        }
      }
      if (["post", "put", "delete"].includes(method) && isOfflineCapable(url) && !(cfg.data instanceof FormData)) {
        const data = await queueWrite(cfg);
        return { data, status: 200, statusText: "OK (queued offline)", headers: {}, config: cfg, _offline: true };
      }
    }
    return Promise.reject(err);
  }
);

/* Auto-sync when connection restores + on app start (heartbeat probes real connectivity) */
if (typeof window !== "undefined") {
  startHeartbeat(api);
  window.addEventListener("online", () => runSync(api));
  setTimeout(() => { if (navigator.onLine) runSync(api); }, 4000);
}
export const triggerSync = () => runSync(api);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  if (d && typeof d.msg === "string") return d.msg;
  return String(d);
}

export const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

export const num = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Number(n || 0));

export const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
};
